import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  FieldArray, FieldArrayPath, FieldArrayPathValue, FieldValues, Path, useFormContext,
} from 'react-hook-form'
import { ADDITIONAL_SUPPLY_CHAIN_ACTOR_ROLE_OPTIONS } from 'features/declaration/models/declarationOptions'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import { blankAdditionalSupplyChainActor } from 'features/declaration/models/blankItems'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import { HouseConsignmentFormValues } from 'features/declaration/house-consignment/models/schema'
import { ConsignmentItemFormValues } from 'features/declaration/consignment-item/models/schema'
import { AdditionalSupplyChainActor } from '../../additional-supply-chain-actor/models/schema'
import FormCollapsibleColumn from '../../../../components/p5/FormCollapsibleColumn'
import FormField from '../../../../components/p5/FormField'
import FormSelect from '../../../../components/p5/FormSelect'
import Input from '../../../../components/p5/Input'
import FormFieldArray from '../../../../components/p5/FormFieldArray'
import DotLeafPaths from '../../../../helpers/dot-leaf-paths'

type AdditionalSupplyChainActorsArrayPath<TForm extends FieldValues> = {
  [P in FieldArrayPath<TForm>]:
  FieldArrayPathValue<TForm, P> extends AdditionalSupplyChainActor[] ? P : never
}[FieldArrayPath<TForm>]

type AdditionalSupplyChainRelPath = DotLeafPaths<AdditionalSupplyChainActor>

interface AdditionalSupplyChainActorsBaseProps<
  TForm extends FieldValues,
  TName extends AdditionalSupplyChainActorsArrayPath<TForm>,
> {
  name: TName
}

function AdditionalSupplyChainActorsBase<
  TForm extends FieldValues,
  TName extends AdditionalSupplyChainActorsArrayPath<TForm> = AdditionalSupplyChainActorsArrayPath<TForm>,
>({ name }: AdditionalSupplyChainActorsBaseProps<TForm, TName>) {
  const { t } = useTranslation()

  const { control } = useFormContext<TForm>()
  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<TForm, AdditionalSupplyChainActor, TName>({
    control,
    name,
  })

  const fieldAtIndex = (index: number, rel: AdditionalSupplyChainRelPath): Path<TForm> => (
    `${name}.${index}.${rel}` as Path<TForm>
  )

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-supply-chain-actor"
        headingLabel={t('declaration.p5.additionalSupplyChainActor')}
        initialIsSectionOpen={false}
      >
        {visibleFields.map((fieldWithIndex) => {
          const additionalSupplyChainActor = fieldWithIndex.field

          return (
            <FormFieldArray
              key={`additionalSupplyChainActor_${additionalSupplyChainActor.id}`}
              titleKey="declaration.p5.sequenceNumber"
              removeItem={() => softRemove(fieldWithIndex.index)}
              sequenceNumber={additionalSupplyChainActor.sequenceNumber + 1}
              hidden={additionalSupplyChainActor.deleted}
              bodyClassName="field-array-row"
            >
              <FormField labelKey="declaration.p5.role">
                <FormSelect
                  <TForm>
                  field={fieldAtIndex(fieldWithIndex.index, 'role')}
                  labelKey="declaration.p5.role"
                  options={ADDITIONAL_SUPPLY_CHAIN_ACTOR_ROLE_OPTIONS}
                  type="sync"
                />
              </FormField>
              <FormField labelKey="declaration.p5.actorIdentificationNr">
                <Input
                  <TForm>
                  type="text"
                  field={fieldAtIndex(fieldWithIndex.index, 'identificationNumber')}
                  labelKey="declaration.p5.actorIdentificationNr"
                />
              </FormField>
            </FormFieldArray>
          )
        })}
        <div className="row">
          <div className="col-12 text-end">
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(blankAdditionalSupplyChainActor as FieldArray<TForm, TName>)}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewActor')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

const FIELD_NAME = 'additionalSupplyChainActor' as const

export function DeclarationAdditionalSupplyChainActors() {
  return <AdditionalSupplyChainActorsBase<TransitOperationFormValues> name={FIELD_NAME} />
}

export function HouseConsignmentAdditionalSupplyChainActors() {
  return <AdditionalSupplyChainActorsBase<HouseConsignmentFormValues> name={FIELD_NAME} />
}

export function ConsignmentItemAdditionalSupplyChainActors() {
  return <AdditionalSupplyChainActorsBase<ConsignmentItemFormValues> name={FIELD_NAME} />
}
